import config from '../config';

/**
 * League season state (weekly results, processed weeks, randomized historical scores).
 * All data is stored on the server — not in localStorage.
 */
class SeasonApi {
  constructor() {
    this.apiUrl = config.API_URL;
  }

  async _request(endpoint, options = {}, currentUser = null) {
    const userId = currentUser?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const url = new URL(`${this.apiUrl}${endpoint}`);
    url.searchParams.append('user_id', userId);

    const response = await fetch(url.toString(), {
      credentials: 'include',
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  teamKeys(teamCount) {
    return Array.from({ length: teamCount }, (_, i) => `Team${i + 1}`);
  }

  emptyWeeklyRecord(teamCount) {
    const rec = {};
    this.teamKeys(teamCount).forEach((team) => {
      rec[team] = { wins: 0, losses: 0, ties: 0, score: 0 };
    });
    return rec;
  }

  async getWeekRecordsPayload(leagueId, currentUser) {
    const data = await this._request(`/leagues/${leagueId}/week-records`, {}, currentUser);
    const raw = data.week_records;
    if (!raw) {
      return { weekly: {} };
    }
    if (raw.weekly && typeof raw.weekly === 'object') {
      return raw;
    }
    return { weekly: {} };
  }

  async saveWeekRecordsPayload(leagueId, payload, currentUser) {
    return this._request(
      `/leagues/${leagueId}/week-records`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.id,
          week_records: payload,
        }),
      },
      currentUser
    );
  }

  async saveWeeklyMatchResults(leagueId, week, weekWlRecords, scoresByTeam, currentUser) {
    const payload = await this.getWeekRecordsPayload(leagueId, currentUser);
    const weekKey = String(week);
    const merged = { ...(payload.weekly[weekKey] || {}) };
    Object.keys(weekWlRecords).forEach((team) => {
      merged[team] = {
        wins: weekWlRecords[team].wins,
        losses: weekWlRecords[team].losses,
        ties: weekWlRecords[team].ties,
        score: scoresByTeam[team] ?? merged[team]?.score ?? 0,
      };
    });
    payload.weekly[weekKey] = merged;
    await this.saveWeekRecordsPayload(leagueId, payload, currentUser);
    return payload;
  }

  async getProcessedWeekNumbers(leagueId, currentUser) {
    const data = await this._request(`/leagues/${leagueId}/week-processed`, {}, currentUser);
    const rows = data.week_processed || [];
    return rows.filter((r) => r.processed).map((r) => r.week);
  }

  async isWeekProcessed(leagueId, week, currentUser) {
    const data = await this._request(
      `/leagues/${leagueId}/week-processed?week=${week}`,
      {},
      currentUser
    );
    const rows = data.week_processed || [];
    return rows.some((r) => r.week === week && r.processed);
  }

  async setWeekProcessed(leagueId, week, processed, currentUser) {
    return this._request(
      `/leagues/${leagueId}/week-processed`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.id,
          week,
          processed,
        }),
      },
      currentUser
    );
  }

  async getRandomPlayerPoint(leagueId, teamName, week, slot, playerId, currentUser, options = {}) {
    const params = new URLSearchParams({
      team_name: teamName,
      week: String(week),
      slot,
      player_id: String(playerId),
    });
    if (options.adminTestReveal) {
      params.append("admin_test_reveal", "1");
    }
    const data = await this._request(
      `/leagues/${leagueId}/random-player-points?${params.toString()}`,
      {},
      currentUser
    );
    const rows = data.random_player_points || [];
    return rows[0] || null;
  }

  async getRandomPlayerPointsForWeek(leagueId, teamName, week, currentUser) {
    const params = new URLSearchParams({
      team_name: teamName,
      week: String(week),
    });
    const data = await this._request(
      `/leagues/${leagueId}/random-player-points?${params.toString()}`,
      {},
      currentUser
    );
    return data.random_player_points || [];
  }

  async setRandomPlayerPoints(leagueId, pointsData, currentUser) {
    return this._request(
      `/leagues/${leagueId}/random-player-points`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.id,
          ...pointsData,
        }),
      },
      currentUser
    );
  }

  _resolveTeamKeys(teamCountOrKeys) {
    return Array.isArray(teamCountOrKeys)
      ? teamCountOrKeys
      : this.teamKeys(teamCountOrKeys);
  }

  buildCumulativeFromWeekly(weeklyByWeek, processedWeeks, teamCountOrKeys) {
    const teamKeys = this._resolveTeamKeys(teamCountOrKeys);
    const cumulative = {};
    teamKeys.forEach((team) => {
      cumulative[team] = { wins: 0, losses: 0, ties: 0, totalPoints: 0 };
    });

    processedWeeks.forEach((w) => {
      const weekRec = weeklyByWeek[String(w)];
      if (!weekRec) return;
      teamKeys.forEach((team) => {
        const row = weekRec[team];
        if (!row) return;
        cumulative[team].wins += row.wins || 0;
        cumulative[team].losses += row.losses || 0;
        cumulative[team].ties += row.ties || 0;
        cumulative[team].totalPoints += row.score ?? row.totalPoints ?? 0;
      });
    });

    return cumulative;
  }

  emptyWeeklyRecordForTeams(teamKeys) {
    const rec = {};
    teamKeys.forEach((team) => {
      rec[team] = { wins: 0, losses: 0, ties: 0, score: 0 };
    });
    return rec;
  }

  async rebuildCumulativeRecords(leagueId, teamCountOrKeys, currentUser) {
    const [payload, processedWeeks] = await Promise.all([
      this.getWeekRecordsPayload(leagueId, currentUser),
      this.getProcessedWeekNumbers(leagueId, currentUser),
    ]);
    return this.buildCumulativeFromWeekly(payload.weekly, processedWeeks, teamCountOrKeys);
  }

  mapRecordsForTeamRecordApi(cumulative) {
    const mapped = {};
    Object.entries(cumulative).forEach(([team, rec]) => {
      mapped[team] = {
        wins: rec.wins || 0,
        losses: rec.losses || 0,
        ties: rec.ties || 0,
        total_points_for: rec.totalPoints ?? rec.total_points_for ?? 0,
      };
    });
    return mapped;
  }

  clearLegacyWeeklyLocalStorage(leagueId) {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (
          key.startsWith('ffb_rand_') ||
          key.startsWith(`ffb_week_processed_${leagueId}_`) ||
          key.startsWith(`ffb_week_records_${leagueId}_`) ||
          key === `ffb_week_records_${leagueId}` ||
          key === `ffb_week_processing_${leagueId}`
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('Could not clear legacy weekly localStorage:', e);
    }
  }

  /** One-time uplift of browser-only weekly data into the API. */
  async migrateLegacyWeeklyLocalStorage(leagueId, teamCount, currentUser) {
    if (!leagueId || !currentUser?.id) return;

    try {
      const payload = await this.getWeekRecordsPayload(leagueId, currentUser);
      let changed = false;

      for (let w = 1; w <= 17; w++) {
        const processedKey = `ffb_week_processed_${leagueId}_${w}`;
        if (localStorage.getItem(processedKey)) {
          const already = await this.isWeekProcessed(leagueId, w, currentUser);
          if (!already) {
            await this.setWeekProcessed(leagueId, w, true, currentUser);
          }
          localStorage.removeItem(processedKey);
        }

        const weekRecordsKey = `ffb_week_records_${leagueId}_${w}`;
        const rawWeek = localStorage.getItem(weekRecordsKey);
        if (rawWeek) {
          try {
            const weekRec = JSON.parse(rawWeek);
            payload.weekly[String(w)] = weekRec;
            changed = true;
          } catch {
            /* ignore */
          }
          localStorage.removeItem(weekRecordsKey);
        }
      }

      if (changed) {
        await this.saveWeekRecordsPayload(leagueId, payload, currentUser);
      }

      const randPrefix = `ffb_rand_${leagueId}_`;
      for (const key of Object.keys(localStorage)) {
        if (!key.startsWith(randPrefix)) continue;
        try {
          const parts = key.split('_');
          const team = parts[3];
          const weekNum = parseInt(parts[4], 10);
          const slot = parts[5];
          const playerId = parts.slice(6).join('_');
          const saved = JSON.parse(localStorage.getItem(key));
          if (saved?.points === undefined) continue;

          await this.setRandomPlayerPoints(
            leagueId,
            {
              team_name: team,
              week: weekNum,
              slot,
              player_id: playerId,
              season: saved.season || '',
              points: saved.points,
              raw_stats: saved.rawStats || null,
            },
            currentUser
          );
          localStorage.removeItem(key);
        } catch {
          /* ignore single key */
        }
      }
    } catch (e) {
      console.warn('Legacy weekly migration skipped:', e);
    }
  }
}

const seasonApi = new SeasonApi();
export default seasonApi;
